import { defineStore } from 'pinia'
import { getProjectList, isOpticalProject, type ProjectListItem } from '@/api/modules/project'
import type { AxiosRequestConfig } from 'axios'

const opticalStatusRequests = new Map<number, Promise<boolean>>()
const opticalStatusRequestVersions = new Map<number, number>()

/** 项目列表接口允许部分历史或权限字段缺失，页面不应因此隐藏合法项目。 */
export type Project = ProjectListItem

export interface TopologyProjectContext {
  projectId: number | null
  templateId?: number | null
  templateTypeId?: number | null
}

export interface ProjectState {
  allProject: ProjectListItem[]
  currentProjectFetching: boolean
  currentProjectFirstFetching: boolean
  currentProject: Project[]
  current: number
  pages: number
  size: number
  total: number
  name: string
  no_result_name: string
  timeSort: 1 | 2
  startTime: string
  endTime: string
  exportProjectPopupShow: boolean
  exportProjectId: number
  selectProjectPopupShow: boolean
  isInitialized: boolean
  currentTemplateId: number | null
  currentTemplateTypeId: number | null
  currentProjectId: number | null
  /** 按项目缓存光传类型判断结果，避免各业务模块重复调用判断接口。 */
  projectOpticalStatusCache: Record<number, boolean>
  /** 模板绑定等外部流程完成后，通知设计中心重新加载当前拓扑。 */
  topologyRefreshKey: number
  currentProjectIsShared: number | null
  mangeProjectPopupShow: boolean
  removeProjectPopupShow: boolean
  removeProjectId: number
  isProjectSwitching: boolean
  isManageEdit?: string
  /** 是否为新用户（首次进入设计中心且无模板） */
  isNewUser: boolean
}

interface ProjectOptions {
  label: string
  value: number
  templateId: number
  isCustomized: number
  isShared: number
  templateTypeId: number | null
}

export const useProjectStore = defineStore('project', {
  state: (): ProjectState => ({
    allProject: [],
    currentProjectFetching: false,
    currentProject: [],
    current: 1,
    pages: 1,
    isManageEdit: '',
    size: (() => {
      const projectPageSize = localStorage.getItem('projectPageSize')
      if (
        typeof projectPageSize === 'string' &&
        projectPageSize !== null &&
        parseInt(projectPageSize) &&
        [10, 20, 50, 100].includes(parseInt(projectPageSize))
      ) {
        return parseInt(projectPageSize)
      } else {
        return 10
      }
    })(),
    total: 0,
    name: '',
    no_result_name: '',
    timeSort: 2, //1 升序  2 降序
    startTime: '',
    endTime: '',
    exportProjectId: 0,
    exportProjectPopupShow: false,
    currentProjectFirstFetching: false,
    selectProjectPopupShow: false,
    isInitialized: false,
    currentProjectId: localStorage.getItem('currentProjectId')
      ? parseInt(localStorage.getItem('currentProjectId') || '')
      : null,
    projectOpticalStatusCache: {},
    currentProjectIsShared: null,
    currentTemplateId: localStorage.getItem('currentTemplateId')
      ? parseInt(localStorage.getItem('currentTemplateId') || '')
      : null,
    currentTemplateTypeId: localStorage.getItem('currentTemplateTypeId')
      ? parseInt(localStorage.getItem('currentTemplateTypeId') || '')
      : null,
    topologyRefreshKey: 0,
    mangeProjectPopupShow: false,
    removeProjectPopupShow: false,
    removeProjectId: 0,
    isProjectSwitching: false,
    isNewUser: false,
  }),
  getters: {
    allProjectOptions: state => {
      const options: ProjectOptions[] = []
      state.allProject.forEach(item => {
        if (!item.isDeleted) {
          options.push({
            label: item.name,
            value: item.id,
            templateId: item.templateId || 0,
            isCustomized: item.isCustomized ?? 0,
            isShared: item.isShared ?? 0,
            templateTypeId: item.templateTypeId ?? item.tempTypeId ?? null,
          })
        }
      })
      return options
    },
    allProjectNames: state => {
      const options: string[] = []
      state.allProject.forEach(item => {
        if (!item.isDeleted) {
          options.push(item.name)
        }
      })
      return options
    },
    /**
     * 当前项目的光传类型；尚未加载时返回 null。
     * 直接读取 cache[projectId]，确保首次写入新 key 时能触发 getter 重算。
     */
    currentProjectIsOptical: state => {
      const projectId = state.currentProjectId
      if (!projectId) return null
      const status = state.projectOpticalStatusCache[projectId]
      return status === undefined ? null : status
    },
    /**
     * 获取指定项目的光传类型；尚未加载时返回 null。
     * 直接读取 cache[projectId]，确保首次写入新 key 时能触发依赖更新。
     */
    projectIsOptical: state => (projectId: number | null | undefined) => {
      if (!projectId) return null
      const status = state.projectOpticalStatusCache[projectId]
      return status === undefined ? null : status
    },
  },
  actions: {
    /**
     * 刷新导航栏和项目选择器共用的完整项目列表。
     * 返回 Promise 让创建项目等流程可在选项更新完成后再切换 currentProjectId，避免下拉框短暂显示旧项目。
     */
    async getAllProjectList() {
      const res = await getProjectList({
        size: 10000,
        current: 1,
      })
      this.allProject = res.data?.records || []
    },
    getProjectList() {
      this.currentProjectFetching = true
      getProjectList({
        size: this.size,
        current: this.current,
        name: this.name,
        startTime: this.startTime,
        endTime: this.endTime,
        timeSort: this.timeSort,
      })
        .then(res => {
          this.currentProjectFetching = false
          this.currentProjectFirstFetching = true
          this.currentProject = res.data?.records ?? []
          this.pages = res.data?.pages ?? 1
          this.total = res.data?.total ?? 0
          this.size = res.data?.size ?? this.size
          this.current = res.data?.current ?? this.current
          this.no_result_name = this.name
        })
        .catch(() => {
          this.currentProjectFetching = false
          this.currentProjectFirstFetching = true
          this.no_result_name = this.name
        })
    },

    resetProjectList() {
      this.currentProject = []
      this.current = 1
      this.pages = 1
      this.size = 10
      this.total = 0
      this.name = ''
      this.startTime = ''
      this.endTime = ''
    },
    showExportProjectPopup(id: number) {
      this.exportProjectId = id
      this.exportProjectPopupShow = true
    },
    hideExportProjectPopup() {
      this.exportProjectPopupShow = false
    },
    showSelectProjectPopup() {
      this.selectProjectPopupShow = true
    },
    hideSelectProjectPopup() {
      this.selectProjectPopupShow = false
    },
    showMangeProjectPopup() {
      this.mangeProjectPopupShow = true
    },
    hideMangeProjectPopup() {
      this.mangeProjectPopupShow = false
    },
    showRemoveProjectPopup() {
      this.removeProjectPopupShow = true
    },
    hideRemoveProjectPopup() {
      this.removeProjectPopupShow = false
    },
    setCurrentTemplateTypeId(templateTypeId: number | null) {
      this.currentTemplateTypeId = templateTypeId
      if (templateTypeId === null) {
        localStorage.removeItem('currentTemplateTypeId')
      } else {
        localStorage.setItem('currentTemplateTypeId', templateTypeId.toString())
      }
    },
    setCurrentProjectId(projectId: number | null) {
      if (this.currentProjectId !== projectId) {
        this.currentProjectIsShared = null
      }
      this.currentProjectId = projectId
      if (projectId) {
        localStorage.setItem('currentProjectId', projectId.toString())
      } else {
        localStorage.removeItem('currentProjectId')
      }
    },
    /**
     * 获取并缓存项目的光传类型。
     * 同一项目的普通请求会复用进行中的 Promise；模板覆盖或新建后可通过 force 强制刷新。
     */
    async loadProjectOpticalStatus(projectId: number, config?: AxiosRequestConfig, force = false): Promise<boolean> {
      if (!Number.isInteger(projectId) || projectId <= 0) {
        throw new Error('Invalid project ID for optical status query')
      }

      const pendingRequest = opticalStatusRequests.get(projectId)
      if (!force && pendingRequest) return pendingRequest

      if (!force && this.projectOpticalStatusCache[projectId] !== undefined) {
        return this.projectOpticalStatusCache[projectId]
      }

      const requestVersion = (opticalStatusRequestVersions.get(projectId) ?? 0) + 1
      opticalStatusRequestVersions.set(projectId, requestVersion)

      const request = isOpticalProject({ projectId }, config)
        .then(response => {
          if (response.code !== 200) {
            throw new Error(response.message || 'Failed to query project optical status')
          }
          const isOptical = Boolean(response.data)
          if (opticalStatusRequestVersions.get(projectId) === requestVersion) {
            // 整对象替换，保证依赖 cache 引用的订阅方也能收到更新。
            this.projectOpticalStatusCache = {
              ...this.projectOpticalStatusCache,
              [projectId]: isOptical,
            }
          }
          return isOptical
        })
        .finally(() => {
          if (opticalStatusRequestVersions.get(projectId) === requestVersion) {
            opticalStatusRequests.delete(projectId)
          }
        })

      opticalStatusRequests.set(projectId, request)
      return request
    },
    setCurrentProjectIsShared(isShared: number | null) {
      this.currentProjectIsShared = isShared
    },
    setCurrentTemplateId(templateId: number | null) {
      this.currentTemplateId = templateId
      if (templateId === null) {
        localStorage.removeItem('currentTemplateId')
      } else {
        localStorage.setItem('currentTemplateId', templateId.toString())
      }
    },
    /** 通知设计中心重新请求当前项目的拓扑数据。 */
    requestTopologyRefresh() {
      this.topologyRefreshKey += 1
    },
    /**
     * 使用拓扑图接口返回的项目上下文原子同步本地状态和持久化缓存。
     * 模板字段缺失时保留当前值，避免部分接口响应清空有效上下文。
     */
    syncFromTopologyResponse(context: TopologyProjectContext) {
      this.setCurrentProjectId(context.projectId)
      if (context.templateId !== undefined) {
        this.setCurrentTemplateId(context.templateId)
      }
      if (context.templateTypeId !== undefined) {
        this.setCurrentTemplateTypeId(context.templateTypeId)
      }
    },
    setProjectSwitching(isSwitching: boolean) {
      this.isProjectSwitching = isSwitching
    },
  },
})
